import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ReferralApi, Referral, Configuration, CourierUser } from '@shared/sdk-v1';
import { getToken, getUserId } from '../state/authSlice';
import { apiConfig } from '../shared/api-config';

interface ReferralWithReferee extends Referral {
  referee?: CourierUser;
}

export const useReferral = () => {
  const userId = useSelector(getUserId);
  const [referrals, setReferrals] = useState<ReferralWithReferee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accessToken = useSelector(getToken);
  const referralApi = new ReferralApi(apiConfig());

  const createReferral = async (referrerId: number, refereeId: number, courierId: number) => {
    try {
      const referral = await referralApi.referralCreate({
        data: {
          referrerId,
          refereeId,
          courierId,
          signUpConfirmedOn: new Date(),
        }
      });
      // After creating a new referral, refresh the referrals list
      fetchReferrals();
      return referral;
    } catch (error) {
      console.error('Error creating referral:', error);
      throw error;
    }
  };

  const fetchReferrals = async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError(null);

    try {
      // Use filter to include the referee (referred user) data
      const filter = JSON.stringify({
        where: {
          referrerId: {
            inq: [userId]
          }
        },
        include: [
          {
            relation: 'referee',
            scope: {
              fields: ['id', 'firstName', 'lastName', 'email']
            }
          }
        ]
      });

      const result = await referralApi.referralFind({ filter });
      setReferrals(result as ReferralWithReferee[]);
    } catch (err) {
      console.error('Error fetching referrals:', err);
      setError('Failed to fetch referrals');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, [accessToken]);

  return {
    referrals,
    isLoading,
    error,
    createReferral,
    refreshReferrals: fetchReferrals
  };
};